import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AnimatedSection = ({ children, className, delay = 0, direction = "up" }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const variants = {
    hidden: {
      opacity: 0,
      y: direction === "up" ? 50 : direction === "down" ? -50 : 0,
      x: direction === "left" ? 50 : direction === "right" ? -50 : 0,
    },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration: 0.6,
        delay: delay,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={variants}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const AboutPage = () => {
  const [team,setTeam] = useState([]);

  async function fetchTeam(){
    const data = fetch('/data.json').then(res => res.json()).then(a => setTeam(a)).catch(e => setTeam([]))
  }

  useEffect(() => {
    fetchTeam()
  },[])

  return (
    <div>
      <motion.section
        className="py-16 bg-blue-900 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-center">About Riverside Bliss Villa</h1>
          <p className="text-xl text-center mt-4 max-w-3xl mx-auto">
            A serene getaway nestled in the heart of Kerala's natural beauty
          </p>
        </div>
      </motion.section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:gap-12">
            <AnimatedSection className="md:w-1/2 mb-8 md:mb-0" direction="left">
              <img
                src="/homestay/outdoor-2.jpg"
                alt="Riverside Bliss Villa"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </AnimatedSection>
            <AnimatedSection className="md:w-1/2" direction="right" delay={0.2}>
              <h2 className="text-3xl font-bold text-blue-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4">
                Riverside Bliss Villa is a charming homestay nestled by the riverside at Second Bridge Kallar in Kerala.
                Our property offers guests a perfect blend of modern comforts and natural beauty, creating an ideal getaway for those seeking tranquility.
              </p>
              <p className="text-gray-600 mb-4">
                Surrounded by lush greenery and the soothing sounds of the river, our villa was designed to provide guests with an authentic experience
                of Kerala's natural landscape while ensuring all modern amenities are available for a comfortable stay.
              </p>
              <p className="text-gray-600">
                We take pride in offering personalized service and creating memorable experiences for our guests, whether they're looking for adventure
                through trekking expeditions or simply wanting to relax by the river.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-blue-900 text-center mb-12">What We Offer</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>,
                title: "Comfortable Accommodations",
                text: "Our well-appointed rooms ensure a comfortable stay with all necessary amenities including clean linens, private bathrooms, and modern furnishings."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>,
                title: "Scenic Riverside Location",
                text: "Located right by the river, offering beautiful views and direct access to the water for swimming and relaxation."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>,
                title: "Campfire Experience",
                text: "Enjoy magical evenings around our campfire, perfect for storytelling and creating lasting memories with family and friends."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>,
                title: "Outdoor BBQ",
                text: "Make use of our barbeque facilities to prepare delicious meals outdoors while enjoying the natural surroundings."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>,
                title: "Trekking Adventures",
                text: "Explore the stunning Varayadumotta area with guided trekking experiences for all skill levels."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>,
                title: "River Swimming",
                text: "Take a refreshing dip in the crystal-clear river waters, accessible directly from our property."
              }
            ].map((service, index) => (
              <AnimatedSection
                key={index}
                className="bg-white p-6 rounded-lg shadow-md"
                delay={0.1 * index}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-800 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-blue-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">
                  {service.text}
                </p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:gap-12">
            <AnimatedSection className="md:w-1/2" direction="up">
              <h2 className="text-3xl font-bold text-blue-900 mb-6">Our Location</h2>
              <p className="text-gray-600 mb-4">
                Riverside Bliss Villa is conveniently located at Second Bridge Kallar in the Varayadumotta area of Kerala,
                just a short drive from main attractions while still offering the peace and quiet of a riverside retreat.
              </p>
              <p className="text-gray-600 mb-4">
                The property enjoys a prime location with direct access to the river and is surrounded by lush
                greenery, creating a perfect setting for nature lovers and those seeking a tranquil escape from city life.
              </p>
              <p className="text-gray-600">
                Our location makes us an ideal base for exploring the surrounding natural attractions, including hiking trails,
                waterfalls, and local wildlife, all within easy reach of our comfortable accommodations.
              </p>
              <div className="mt-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-3">Nearby Attractions:</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    "Ponmudi Hill Station (15 minutes)",
                    "Varayadumotta Trekking Trails (5 minutes)",
                    "Kallar Waterfalls (15 minutes)",
                    "Local Spice Gardens (20 minutes)",
                    "Traditional Kerala Villages (25 minutes)"
                  ].map((attraction, index) => (
                    <motion.li
                      key={index}
                      className="flex items-start"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + (index * 0.1), duration: 0.4 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {attraction}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
            <AnimatedSection className="md:w-1/2 mt-8 md:mt-0" direction="up" delay={0.3}>
              <div className="rounded-lg overflow-hidden shadow-lg h-96 bg-gray-200">
                <iframe
                  title='River Side Bliss Vills'
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3943.7730260952785!2d77.12840249999999!3d8.7130911!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05cd005c8dfe7f%3A0x2c31dd7ce194c6bb!2sRiverside%20Bliss%20villa%20home%20stay!5e0!3m2!1sen!2sin!4v1741620474940!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade">
                </iframe>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-blue-900 text-center mb-12">Meet Our Team</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <AnimatedSection
                key={index}
                className="bg-white rounded-lg shadow-md overflow-hidden"
                delay={0.2 * index}
              >
                <div className="h-64 bg-gray-200">
                  <img
                    src={member.image || "/assets/images/avatar.jpg"}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/images/avatar.jpg"; }}
                  />

                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-blue-900">{member.name}</h3>
                  <p className="text-blue-600 mb-4">{member.position}</p>
                  <p className="text-gray-600">
                    {member.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <AnimatedSection>
              <h2 className="text-3xl font-bold text-blue-900">Our Commitment to Sustainability</h2>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                At Riverside Bliss Villa, we believe in preserving the natural beauty that surrounds us
              </p>
            </AnimatedSection>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>,
                title: "Solar Power",
                text: "We utilize solar energy for heating water and supplementing our electricity needs."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>,
                title: "Local Sourcing",
                text: "We source food and materials locally, supporting the community and reducing our carbon footprint."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>,
                title: "Water Conservation",
                text: "We implement water-saving practices throughout our property and encourage responsible water use."
              },
              {
                icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>,
                title: "Waste Reduction",
                text: "We minimize single-use plastics and practice proper waste segregation and recycling."
              }
            ].map((initiative, index) => (
              <AnimatedSection
                key={index}
                className="p-4"
                delay={0.15 * index}
                direction={index % 2 === 0 ? "up" : "down"}
              >
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-800 mb-4">
                  {initiative.icon}
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">{initiative.title}</h3>
                <p className="text-gray-600">
                  {initiative.text}
                </p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <AnimatedSection className="text-center md:text-left md:w-2/3" direction="left">
              <h2 className="text-3xl font-bold">Experience the Beauty of Riverside Living</h2>
              <p className="mt-4 text-lg opacity-90">
                Book your stay today and discover why our guests keep coming back to Riverside Bliss Villa.
              </p>
            </AnimatedSection>
            <AnimatedSection className="mt-8 md:mt-0 text-center md:text-right" direction="right">
              <motion.a
                href="/contact"
                className="inline-block bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold px-6 py-3 rounded-lg text-lg transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.a>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;